import * as PIXI from "pixi.js";
import { Assets } from "pixi.js";
import L from "leaflet";
import { getMarkerTheme } from "./themes";

const drawMarker = ({
  m,
  container,
  project,
  pixiOverlay,
  colorMode,
  editable,
  showLabel,
  elementSelected,
  handleUpdatePositionElement,
  onClick,
  map,
  rulerMode,
  spriteByIdRef,
  explosionsRef,
  configElementDataMap,
  theme,
  labelTexture,
  editMode,
  tema = "classico",
}) => {
  if (!m?.iconId || !m?.baseIcon) return;

  const { utils } = pixiOverlay;
  const textures = {
    texture: Assets.get(m.iconId),
    markerTextureLight: Assets.get("marker_circle_light"),
    markerTextureDark: Assets.get("marker_circle_dark"),
    markerTextureTransparent: Assets.get("marker_circle_transparent"),
    markerCircleBorder: Assets.get("marker_circle_border"),
    markerDisaster: Assets.get("marker_disaster"),
  };

  if (!Object.values(textures).every(Boolean)) return;

  if (!(m?.position?.[0] && m?.position?.[1])) return;
  const point = project(L.latLng(...m?.position));

  const node = new PIXI.Container();
  node.position.set(point.x, point.y);
  node.markerId = m.id ?? m.tempId;

  const markerTheme = getMarkerTheme(tema);

  const { spriteRefs, state } = markerTheme.buildVisuals({
    m,
    node,
    textures,
    colorMode,
    editable,
    editMode,
    theme,
    showLabel,
    labelTexture,
  });

  node.eventMode = "static";
  node.cursor = "pointer";

  let dragging = false;
  let dragOffset = { x: 0, y: 0 };

  const { start, move, end, endOutside } = markerTheme.dragEvents;

  if (
    editable &&
    (m?.id === elementSelected?.id || m?.id === elementSelected?.tempId)
  ) {
    const onDragStart = (e) => {
      if (rulerMode) return;

      e.stopPropagation();
      dragging = true;

      node.alpha = 0.6;
      map.dragging.disable();

      const localPosition = node.parent.toLocal(e.global);

      dragOffset.x = node.x - localPosition.x;
      dragOffset.y = node.y - localPosition.y;

      const stage = node.stage || node.parent;

      stage.on(move, onDragMove);
      stage.on(end, onDragEnd);
      stage.on(endOutside, onDragEnd);
    };

    const onDragMove = (e) => {
      if (!dragging) return;

      const localPosition = node.parent.toLocal(e.global);
      node.position.set(
        localPosition.x + dragOffset.x,
        localPosition.y + dragOffset.y,
      );
    };

    const onDragEnd = () => {
      if (!dragging) return;

      dragging = false;
      node.alpha = 1;
      map.dragging.enable();

      const newPoint = { x: node.position.x, y: node.position.y };
      const newLatLng = utils.layerPointToLatLng(newPoint);
      handleUpdatePositionElement(null, {
        lat: newLatLng.lat,
        lng: newLatLng.lng,
      });

      const stage = node.stage || node.parent;
      stage.off(move, onDragMove);
      stage.off(end, onDragEnd);
      stage.off(endOutside, onDragEnd);
    };

    node.on(start, onDragStart);
  }

  node.on(start, (e) => {
    if (!dragging && !rulerMode) onClick(e, m);
  });

  container.addChild(node);

  const baseCircleRadius = (spriteRefs.circle?.width ?? 0) / 2;
  const baseSpriteRadius = (spriteRefs.sprite?.width ?? 0) / 2;
  const baseRadiusPx = Math.max(baseSpriteRadius, baseCircleRadius);

  // Guarda referência dos sprites
  spriteByIdRef.current.set(m.id ?? m.tempId, {
    ...spriteRefs,
    baseRadiusPx,
  });

  const explosionGraphic = markerTheme.buildExplosion(
    { m, editable, configElementDataMap, textures },
    state,
  );

  if (explosionGraphic) {
    node.addChildAt(explosionGraphic, 0);
    explosionsRef.current.set(m.id, { graphic: explosionGraphic, t: 0 });
  } else {
    // Remove explosão se não tiver mais alertas críticos
    explosionsRef.current.delete(m.id);
  }
};

export default drawMarker;
